import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useChangePassword } from '@/lib/hooks'
import { passwordChangeSchema, type PasswordChangeData } from '@/lib/validation'

export function PasswordChangeForm() {
  const { t } = useTranslation()
  const changePasswordMutation = useChangePassword()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PasswordChangeData>({
    resolver: zodResolver(passwordChangeSchema),
  })

  const handleFormSubmit = async (data: PasswordChangeData) => {
    try {
      await changePasswordMutation.mutateAsync(data)
      reset()
      // TODO: Show success message
    } catch (error) {
      console.error('Failed to change password:', error)
      // TODO: Show error message
    }
  }

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="space-y-3">
          <Label htmlFor="current_password" variant="required">
            {t('profile.currentPassword', 'Huidig wachtwoord')}
          </Label>
          <Input
            id="current_password"
            type="password"
            {...register('current_password')}
            placeholder="Voer uw huidige wachtwoord in"
            error={!!errors.current_password}
            aria-invalid={!!errors.current_password}
          />
          {errors.current_password && (
            <p className="text-sm text-destructive mt-1" role="alert">
              {errors.current_password.message}
            </p>
          )}
        </div>

        <div className="space-y-3">
          <Label htmlFor="new_password" variant="required">
            {t('profile.newPassword', 'Nieuw wachtwoord')}
          </Label>
          <Input
            id="new_password"
            type="password"
            {...register('new_password')}
            placeholder="Voer een nieuw wachtwoord in"
            error={!!errors.new_password}
            aria-invalid={!!errors.new_password}
          />
          {errors.new_password && (
            <p className="text-sm text-destructive mt-1" role="alert">
              {errors.new_password.message}
            </p>
          )}
        </div>

        <div className="space-y-3">
          <Label htmlFor="confirm_password" variant="required">
            {t('profile.confirmPassword', 'Bevestig nieuw wachtwoord')}
          </Label>
          <Input
            id="confirm_password"
            type="password"
            {...register('confirm_password')}
            placeholder="Herhaal het nieuwe wachtwoord"
            error={!!errors.confirm_password}
            aria-invalid={!!errors.confirm_password}
          />
          {errors.confirm_password && (
            <p className="text-sm text-destructive mt-1" role="alert">
              {errors.confirm_password.message}
            </p>
          )}
        </div>

        <Button
          type="submit"
          variant="gradient"
          className="w-full"
          loading={changePasswordMutation.isPending}
          disabled={changePasswordMutation.isPending}
        >
          {changePasswordMutation.isPending
            ? t('algemeen.laden', 'Laden...')
            : t('profile.changePassword', 'Wachtwoord wijzigen')}
        </Button>
      </form>
    </div>
  )
}
